import {
  API_BASE_PATH,
  type AuthenticatedUser,
  type ChangePasswordInput,
  type LoginInput,
  type LoginResult,
  type SessionSummary,
} from '@taskflow/shared';
import { config } from '../../config/env';
import { http } from '../../services/httpClient';

/**
 * Auth API calls.
 *
 * Thin by design: the schemas and result types come from `@taskflow/shared`, so this
 * file cannot drift from the Worker. If an endpoint's shape changes, this stops
 * compiling.
 */

export const authApi = {
  me: () => http.get<AuthenticatedUser>('/auth/me'),

  login: (input: LoginInput) => http.post<LoginResult>('/auth/login', input),

  logout: () => http.post<void>('/auth/logout'),

  logoutOthers: () => http.post<{ revoked: number }>('/auth/logout-others'),

  sessions: () => http.get<SessionSummary[]>('/auth/sessions'),

  forgotPassword: (email: string) => http.post<void>('/auth/forgot-password', { email }),

  resetPassword: (input: { token: string; password: string; confirmPassword: string }) =>
    http.post<void>('/auth/reset-password', input),

  changePassword: (input: ChangePasswordInput) => http.post<void>('/auth/change-password', input),
};

/**
 * Sends the browser to Google.
 *
 * A full navigation, not fetch: the OAuth flow requires the browser to visit Google
 * and be redirected back, which an XHR cannot do. `redirectTo` carries the page the
 * user was trying to reach so they land there instead of on a generic dashboard —
 * the server rejects anything that is not a same-site path.
 */
export function startGoogleSignIn(redirectTo?: string): void {
  const url = new URL(`${config.apiBaseUrl}${API_BASE_PATH}/auth/google`, window.location.origin);
  if (redirectTo) url.searchParams.set('redirectTo', redirectTo);
  window.location.assign(url.toString());
}

/**
 * Maps the `?error=` code from a failed Google callback to a message.
 *
 * The server redirects with a code rather than text so the wording lives here, with
 * the rest of the UI copy — and so the API never has to guess what the user should
 * read.
 */
export function googleErrorMessage(code: string | null): string | null {
  switch (code) {
    case null:
      return null;
    case 'not_provisioned':
      return 'There is no TaskFlow account for that address yet. Ask an administrator to add you.';
    case 'domain_not_allowed':
      return 'Only Cuddles Foundation accounts can sign in here.';
    case 'email_not_verified':
      return 'That Google account has an unverified email address.';
    case 'account_suspended':
      return 'That account has been suspended. Please contact an administrator.';
    case 'google_cancelled':
      return 'Sign-in was cancelled.';
    default:
      return 'Google sign-in could not be completed. Please try again.';
  }
}
